interface InstanceStatus {
  instanceId: string;
  // Legacy fields (for backward compatibility)
  status: 'online' | 'offline' | 'degraded' | 'connecting' | 'reconnecting';
  apiReachable?: boolean;
  // New simplified status fields
  apiStatus: 'connected' | 'not_connected';
  serviceStatus: 'started' | 'stopped';
  lastChecked: string;
}

type EventHandler = (...args: any[]) => void;

// Use the same API_BASE_URL logic as api.ts
const API_BASE_URL =
  import.meta.env.PROD
    ? '/api'
    : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api');

class SSEService {
  private eventSource: EventSource | null = null;
  private isConnected: boolean = false;
  private reconnectTimer: number | null = null;
  private reconnectDelay: number = 3000; // Start with 3 seconds
  private maxReconnectDelay: number = 30000; // Max 30 seconds
  private apiUrl: string = `${API_BASE_URL}/events`;
  private eventHandlers: Record<string, EventHandler[]> = {};
  private maxRetries: number = 30; // Increase max retries
  private retryCount: number = 0;
  private lastReceivedHeartbeat: number = Date.now();
  private heartbeatInterval: number | null = null;
  private connectionsCount: number = 0;
  private isConnecting: boolean = false;
  private connectionStabilityTimer: number | null = null; // New timer to ensure stable connections

  constructor() {
    console.log('SSE service initialized');
    // Auto-reconnect if the page becomes visible again (browser tab was inactive)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !this.isConnected && !this.isConnecting) {
        console.log('Page became visible, reconnecting...');
        this.connect();
      }
    });
    
    // Clean up connection on page unload
    window.addEventListener('beforeunload', () => {
      console.log('Page unloading, closing SSE connection');
      this.close();
    });
  }

  // Connect to the SSE endpoint
  connect(): void {
    // Skip if already connecting or connected
    if (this.isConnecting || (this.eventSource && this.isConnected)) {
      console.log('SSE already connected or connecting, skipping connection request');
      return;
    }

    this.isConnecting = true;

    // Check retry limit
    if (this.retryCount >= this.maxRetries) {
      console.log(`Maximum retry count (${this.maxRetries}) reached, stopping reconnection attempts`);
      this.emitEvent('maxRetriesReached');
      this.isConnecting = false;
      return;
    }

    // Make sure any existing connection is closed properly
    this.close();

    try {
      console.log('Connecting to SSE endpoint:', this.apiUrl);
      
      // Create a new EventSource with retry set to 5 seconds
      this.eventSource = new EventSource(this.apiUrl);
      this.connectionsCount++;
      console.log(`Created new EventSource connection (total created: ${this.connectionsCount})`);

      // Connection opened
      this.eventSource.onopen = () => {
        console.log('SSE connection established');
        this.isConnected = true;
        this.isConnecting = false;
        this.reconnectDelay = 3000; // Reset reconnect delay on successful connection
        this.retryCount = 0; // Reset retry count on successful connection
        this.lastReceivedHeartbeat = Date.now();
        this.startHeartbeatMonitor();
        this.emitEvent('connected');
        
        // Clear any existing connection stability timer
        if (this.connectionStabilityTimer) {
          clearTimeout(this.connectionStabilityTimer);
        }
        
        // Set a timer to ensure the connection remains stable for at least 10 seconds
        // This prevents rapid connect/disconnect cycles
        this.connectionStabilityTimer = window.setTimeout(() => {
          console.log('Connection has been stable for 10 seconds');
          this.connectionStabilityTimer = null;
        }, 10000);
      };

      // Connection error
      this.eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        
        // If the connection was very brief (less than the stability period)
        // increase the retry count more aggressively to prevent rapid cycling
        if (this.connectionStabilityTimer) {
          console.warn('Connection error during stability period, may indicate server issues');
          this.retryCount += 2; // Increment more to slow down reconnection attempts
          clearTimeout(this.connectionStabilityTimer);
          this.connectionStabilityTimer = null;
        } else {
          this.retryCount++; // Normal increment for established connections
        }
        
        this.isConnected = false;
        this.isConnecting = false;
        this.close();
        this.scheduleReconnect();
        this.emitEvent('error', error);
      };

      // Listen for all messages, including heartbeats
      this.eventSource.onmessage = () => {
        // Always update heartbeat timestamp for any message
        this.lastReceivedHeartbeat = Date.now();
        // Don't log standard messages - they're too noisy
      };

      // Listen for heartbeat events (explicit format)
      this.eventSource.addEventListener('heartbeat', () => {
        // Just update the timestamp - no need to log every heartbeat
        this.lastReceivedHeartbeat = Date.now();
      });

      // Listen for instance status updates
      this.eventSource.addEventListener('instance_status', (event: MessageEvent) => {
        try {
          // Update heartbeat timestamp - any message counts as a sign that connection is alive
          this.lastReceivedHeartbeat = Date.now();
          
          const data = JSON.parse(event.data) as InstanceStatus;
          console.log('Instance status update received:', data);
          this.emitEvent('instanceStatus', data);
        } catch (error) {
          console.error('Error parsing instance status data:', error);
        }
      });

      // Listen for deployment status updates
      this.eventSource.addEventListener('deployment_status', (event: MessageEvent) => {
        try {
          // Update heartbeat timestamp
          this.lastReceivedHeartbeat = Date.now();
          
          const data = JSON.parse(event.data);
          console.log('Deployment status update received:', data);
          this.emitEvent('deployment_status', data);
        } catch (error) {
          console.error('Error parsing deployment status data:', error);
        }
      });

      // Listen for NFS deployment status updates
      this.eventSource.addEventListener('nfs_deployment_status', (event: MessageEvent) => {
        try {
          this.lastReceivedHeartbeat = Date.now();
          const data = JSON.parse(event.data);
          console.log('NFS deployment status update received:', data);
          this.emitEvent('deployment_status', data); // Re-emit as 'deployment_status' for NFS
        } catch (error) {
          console.error('Error parsing nfs_deployment_status data:', error);
        }
      });

      // Listen for connected event
      this.eventSource.addEventListener('connected', (event: MessageEvent) => {
        try {
          // Update heartbeat timestamp
          this.lastReceivedHeartbeat = Date.now();
          
          const data = JSON.parse(event.data);
          console.log('Connected to SSE server:', data);
          this.emitEvent('serverConnected', data);
        } catch (error) {
          console.error('Error parsing connected event data:', error);
        }
      });

      // Listen for backend updated events
      this.eventSource.addEventListener('backend_updated', (event: MessageEvent) => {
        try {
          this.lastReceivedHeartbeat = Date.now();
          const data = JSON.parse(event.data);
          console.log('SSE: backend_updated event received:', data);
          this.emitEvent('backend_updated', data);
        } catch (error) {
          console.error('Error parsing backend_updated event data:', error);
        }
      });

      // Listen for backend deleted events
      this.eventSource.addEventListener('backend_deleted', (event: MessageEvent) => {
        try {
          this.lastReceivedHeartbeat = Date.now();
          const data = JSON.parse(event.data);
          console.log('SSE: backend_deleted event received:', data);
          this.emitEvent('backend_deleted', data);
        } catch (error) {
          console.error('Error parsing backend_deleted event data:', error);
        }
      });

      // Listen for HAProxy instance status updates
      this.eventSource.addEventListener('haproxy_instance_status', (event: MessageEvent) => {
        try {
          this.lastReceivedHeartbeat = Date.now();
          const data = JSON.parse(event.data);
          console.log('HAProxy instance status update received:', data);
          this.emitEvent('haproxyInstanceStatus', data);
        } catch (error) {
          console.error('Error parsing haproxy instance status data:', error);
        }
      });

      // Listen for NFS instance status updates
      this.eventSource.addEventListener('nfs_instance_status', (event: MessageEvent) => {
        try {
          this.lastReceivedHeartbeat = Date.now();
          const data = JSON.parse(event.data);
          console.log('NFS instance status update received:', data);
          this.emitEvent('nfsInstanceStatus', data);
        } catch (error) {
          console.error('Error parsing nfs_instance_status data:', error);
        }
      });

      // Log all received SSE events for debugging
      this.eventSource.addEventListener('open', () => {
        console.log('[SSE] Connection opened');
      });
      this.eventSource.addEventListener('error', (e) => {
        console.log('[SSE] Connection error', e);
      });
      // Generic event listener for all events
      const logAllEvents = (event: MessageEvent) => {
        // Try to get the event type from the event object
        // @ts-ignore
        const eventType = event.type || event.event || 'unknown';
        try {
          const data = event.data ? JSON.parse(event.data) : event.data;
          console.log(`[SSE] Event: ${eventType}`, data);
        } catch (err) {
          console.log(`[SSE] Event: ${eventType}`, event.data);
        }
      };
      // List of all event types to log
      [
        'instance_status',
        'nfs_instance_status',
        'haproxy_instance_status',
        'deployment_status',
        'nfs_deployment_status',
        'backend_updated',
        'backend_deleted',
        'connected',
        'heartbeat',
      ].forEach(eventType => {
        this.eventSource!.addEventListener(eventType, logAllEvents);
      });
    } catch (error) {
      console.error('Error initializing SSE connection:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  // Start monitoring heartbeats
  private startHeartbeatMonitor(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Check every minute if we've received a heartbeat recently
    // (server sends heartbeats every 30s)
    this.heartbeatInterval = window.setInterval(() => {
      const now = Date.now();
      const elapsed = now - this.lastReceivedHeartbeat;
      
      // Only log when we have a potential issue
      if (elapsed > 60000) {
        // Too long without heartbeat - reconnect
        console.warn(`No heartbeat received in ${Math.floor(elapsed/1000)}s, reconnecting...`);
        this.close();
        this.connect();
      }
    }, 60000); // Check every minute
  }

  // Close the SSE connection
  close(): void {
    if (this.eventSource) {
      console.log('Closing SSE connection');
      this.eventSource.close();
      this.eventSource = null;
    }
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.connectionStabilityTimer) {
      clearTimeout(this.connectionStabilityTimer);
      this.connectionStabilityTimer = null;
    }
    
    this.isConnected = false;
  }

  // Schedule a reconnection attempt
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Calculate reconnect delay with exponential backoff based on retry count
    const currentDelay = Math.min(this.reconnectDelay * Math.pow(1.5, Math.min(this.retryCount, 8)), this.maxReconnectDelay);
    
    console.log(`Scheduling reconnect in ${Math.floor(currentDelay/1000)}s (retry ${this.retryCount}/${this.maxRetries})`);
    this.emitEvent('reconnecting', currentDelay);
    
    this.reconnectTimer = window.setTimeout(() => {
      console.log('Attempting to reconnect to SSE endpoint');
      this.connect();
    }, currentDelay);
  }

  // Register an event handler
  on(event: string, handler: EventHandler): void {
    if (!this.eventHandlers[event]) {
      this.eventHandlers[event] = [];
    }
    this.eventHandlers[event].push(handler);
  }

  // Unregister an event handler
  off(event: string, handler: EventHandler): void {
    if (!this.eventHandlers[event]) return;
    
    this.eventHandlers[event] = this.eventHandlers[event].filter(h => h !== handler);
    
    if (this.eventHandlers[event].length === 0) {
      delete this.eventHandlers[event];
    }
  }

  // Emit an event to all registered handlers
  private emitEvent(event: string, ...args: any[]): void {
    if (!this.eventHandlers[event]) return;
    
    for (const handler of this.eventHandlers[event]) {
      try {
        handler(...args);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    }
  }

  // Check if SSE connection is active
  isActive(): boolean {
    return this.isConnected;
  }
}

export default new SSEService(); 